/**
 * Context Menu Hook
 * Manages context menu state and positioning
 */

import { useCallback } from 'react';
import { useUIStore } from '@flow-studio/store';
import { ContextMenuState } from '@flow-studio/types';

/** Minimal shape of the React Flow instance methods this hook needs. */
interface FlowPositionResolver {
  screenToFlowPosition: (position: { x: number; y: number }) => { x: number; y: number };
}

/**
 * @param reactFlowInstance Optional — when provided, each opened menu also records its
 * flow-space (canvas) position so it can stay anchored to its target while the canvas
 * pans/zooms, instead of being left behind at a stale screen coordinate.
 */
export const useContextMenu = (reactFlowInstance?: FlowPositionResolver | null) => {
  const { setContextMenu, closeContextMenu, contextMenu } = useUIStore();

  const openContextMenu = useCallback(
    (menu: ContextMenuState) => {
      setContextMenu(menu);
    },
    [setContextMenu]
  );

  const toFlowPosition = useCallback(
    (clientX: number, clientY: number) =>
      reactFlowInstance?.screenToFlowPosition({ x: clientX, y: clientY }),
    [reactFlowInstance]
  );

  const handleNodeContextMenu = useCallback(
    (event: React.MouseEvent, nodeId: string, data?: any) => {
      event.preventDefault();
      event.stopPropagation();

      openContextMenu({
        type: 'node',
        position: { x: event.clientX, y: event.clientY },
        flowPosition: toFlowPosition(event.clientX, event.clientY),
        targetId: nodeId,
        data,
      });
    },
    [openContextMenu, toFlowPosition]
  );

  const handleEdgeContextMenu = useCallback(
    (event: React.MouseEvent, edgeId: string, data?: any) => {
      event.preventDefault();
      event.stopPropagation();

      openContextMenu({
        type: 'edge',
        position: { x: event.clientX, y: event.clientY },
        flowPosition: toFlowPosition(event.clientX, event.clientY),
        targetId: edgeId,
        data,
      });
    },
    [openContextMenu, toFlowPosition]
  );

  const handleHandleContextMenu = useCallback(
    (event: React.MouseEvent, nodeId: string, handleId: string, data?: any) => {
      event.preventDefault();
      event.stopPropagation();

      openContextMenu({
        type: 'handle',
        position: { x: event.clientX, y: event.clientY },
        flowPosition: toFlowPosition(event.clientX, event.clientY),
        targetId: `${nodeId}-${handleId}`,
        data: { nodeId, handleId, ...data },
      });
    },
    [openContextMenu, toFlowPosition]
  );

  const handleCanvasContextMenu = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();

      openContextMenu({
        type: 'canvas',
        position: { x: event.clientX, y: event.clientY },
        flowPosition: toFlowPosition(event.clientX, event.clientY),
      });
    },
    [openContextMenu, toFlowPosition]
  );

  return {
    contextMenu,
    openContextMenu,
    closeContextMenu,
    handleNodeContextMenu,
    handleEdgeContextMenu,
    handleHandleContextMenu,
    handleCanvasContextMenu,
  };
};
